/* eslint-disable prefer-template */
/* eslint-disable camelcase */
import lowerCase from 'lodash/lowerCase';
import shuffle from 'lodash/shuffle';
import { format, parse } from 'date-fns';
import { ScheduleTypes } from '@/apps/job_post/types';

/*
Example prompt:
Please act as a parent/ caregiver hiring a babysitter. The goal is to generate a comprehensive job description.

  The generated job description will use simple language and descriptive action verbs in the present tense.
  It will avoid abbreviations, acronyms, ambiguous terms, and gender-specific language.
  It will also be comprehensive and thoroughly account for the most likely and common job description content based on what is known about the job title.
  It will be in paragraph form.

  These are the details about the job:
  1. Schedule: The start date is Fri Feb 23 2024 17:08:45 GMT-0600 (Central Standard Time)  The start time is 2024-02-23T21:00:00-06:00 and the start time is flexible The end time is 2024-02-23T23:30:00-06:00
  2. This job is for 2 children.
  3. They are in the age group: infant
  4. These are the requirements: multiples experience, bedtime
  false

  Remove flowery language from the description.
  Be sure to keep this description under 500 characters
*/

// Candidate opening intro phrases to reduce positional bias
const OPENING_INTROS = [
  'Hi there!',
  'We’re looking for help',
  'Hello sitters!',
  'Hi',
  'Were looking for',
  'Hello!',
  'Hello,',
  'Could you help us?',
  'We’re looking for a',
  'Help our',
  'We want a',
];

const formatTime12Hour = (time24) => {
  const parsed = parse(time24, 'HH:mm', new Date());
  return format(parsed, 'h:mm a'); // e.g., "4:30 AM"
};

const careType = (state) => {
  return state.scheduleType === ScheduleTypes.NANNY ? ScheduleTypes.NANNY : 'babysitter';
};

const schedule = (state) => {
  switch (state.scheduleType) {
    case ScheduleTypes.ONE_TIME:
      return {
        startDate: state.schedule?.oneTimeJobSchedule?.startDate,
        startTime: state.schedule?.oneTimeJobSchedule?.startTime,
        endTime: state.schedule?.oneTimeJobSchedule?.endTime,
        startTimeFlexible: state.schedule?.oneTimeJobSchedule?.isStartTimeFlexible,
      };
    case ScheduleTypes.REGULAR:
      return {
        days: state.schedule?.regularJobSchedule?.days,
        endTime: state.schedule?.regularJobSchedule?.endTime,
        startDateFlexible: state.schedule?.regularJobSchedule?.isStartDateFlexible,
        startTimeFlexible: false, // Not set in workflow
        startDate: state.schedule?.regularJobSchedule?.startDate,
        startTime: state.schedule?.regularJobSchedule?.startTime,
      };
    case ScheduleTypes.OCCASIONAL:
      return {
        startDateFlexible: state.schedule?.occasionalJobSchedule?.isStartDateFlexible,
        startDate: state.schedule?.occasionalJobSchedule?.startDate,
      };
    case ScheduleTypes.NANNY:
      return {
        hours: state.schedule?.nannyJobSchedule?.hours,
        startDate: state.schedule?.nannyJobSchedule?.startDate,
        startDateFlexible: state.schedule?.nannyJobSchedule?.isStartDateFlexible,
      };
    default:
      return {};
  }
};

const scheduleDetails = (state) => {
  const { startTime, startDate, endTime, startTimeFlexible, startDateFlexible, days, hours } = schedule(state);
  const convertedStartTime = startTime ? formatTime12Hour(startTime) : null;
  const convertedEndTime = endTime ? formatTime12Hour(endTime) : null;

  const details =
    `The start date is ${startDate} ${startDateFlexible ? 'and the start date is flexible ' : ''}` +
    `${convertedStartTime ? ` The start time is ${convertedStartTime}` : ''}` +
    `${startTimeFlexible ? ' and the start time is flexible' : ''}` +
    `${convertedEndTime ? ` The end time is ${convertedEndTime}` : ''}` +
    `${days ? ` The days of week include ${days.join(', ').toLowerCase()}` : ''}` +
    `${hours ? ` The nanny is available ${hours.map((type) => lowerCase(type)).join(', ')}` : ''}`;
  return details;
};

// TODO: adjust based on legal feedback
const jobContentPrompt = (contentType, state) => {
  const { requirements, languages, numberOfChildren, ageGroups } = state;

  // Shuffle the opening intros to avoid bias toward the first option
  const randomizedIntros = shuffle(OPENING_INTROS)
    .map((intro) => `"${intro}"`)
    .slice(0, 3)
    .join(', ');

  return `Please act as a parent/ caregiver hiring a ${careType(
    state
  )}. The goal is to generate a comprehensive job ${contentType}.
  You must keep this ${contentType} ${contentType === 'description' ? 'less than 500 characters' : 'between 10 and 80 characters'}

  The generated job ${contentType} will use simple language and descriptive action verbs in the present tense.
  It will avoid abbreviations, acronyms, ambiguous terms, and gender-specific language.
  It will also be comprehensive and thoroughly account for the most likely and common job description content based on what is known about the job title.
  ${contentType === 'description' ? 'It will be in paragraph form. ' : ''}
  ${contentType === 'description' ? `Use different opening lines (e.g., ${randomizedIntros}) and mix sentence lengths. Pick one randomly to avoid repetition and detection of AI.` : ''}

  Rules:
  - Use contractions and natural phrasing.
  - when child age is JUNIOR_HIGH, use the term 'middle school' and not 'junior high' in the job description
  - Do not use abbreviations, acronyms, ambiguous terms, and gender-specific language.
  - Do not use flowery language.
  - Make sure the content feels distinct, warm, and human.

  These are the details about the job (include them in a random order):
  ${shuffle([
    '- Schedule: ' + scheduleDetails(state),
    '- This job is for ' + numberOfChildren + (numberOfChildren > 1 ? ' children' : ' child'),
    '- They are in the age group: ' + ageGroups.join(', '),
    '- These are the requirements: ' +
      requirements
        .filter((r) => r !== 'COMFORTABLE_WITH_PETS')
        .map((r) => lowerCase(r))
        .join(', '),
    languages.length > 0 ? `"- Sitter needs to speak: ${languages.join(', ')}"` : '',
  ]).join('\n')};

  ${contentType === 'description' && Math.random() > 0.7 ? 'Use paragraphs to break up the text.' : ''}

  Remove flowery language from the ${contentType}. Avoiding using 'caring' as an adjective.`;
};

export const jobDescriptionPrompt = (state) => {
  return jobContentPrompt('description', state);
};

export const jobHeadlinePrompt = (state) => {
  return jobContentPrompt('headline', state);
};
